import React, { useContext, useMemo } from 'react';

import { Typography, Stepper, Step, StepLabel, Grid, StepContent } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { FiberManualRecord as CircleIcon, CheckCircle as CheckCircleIcon } from '@material-ui/icons';

import { Context } from './context';
import { css } from '@emotion/react';
import { Body1, Body2, Subtitle1 } from './blocks';
import { PrimaryButton } from '@/styles/blocks';
import { useMutation } from '@apollo/client';
import { UPDATE_ORDER_BY_ID } from '@/graphql/mutations/orders';
import { CONFIRMATION_SOURCE } from '@/constants/confirmationSource';
import * as Sentry from '@sentry/react';
import { genAttributes } from '@onward-delivery/core';
import { colors } from '@/styles';
import { ORDER_COMPLETE_STATUSES, STEP_KEYS } from './constants';
import { buildTrackerSteps } from './trackerSteps';
import { formatStamp } from './utilities';

const QontoStepIconRoot = styled('div')(({ theme, owner_state }) => ({
    color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#eaeaf0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
    ...(owner_state.active && {
        color: '#784af4',
    }),
    '& .QontoStepIcon-completedIcon': {
        color: '#784af4',
        zIndex: 1,
        fontSize: 18,
    },
    '& .QontoStepIcon-circle': {
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: '#555',
    },
}));

function QontoStepIcon(props) {
    const { active, completed, className } = props;

    return (
        <QontoStepIconRoot owner_state={{ active }} className={className}>
            {completed ? (
                <CheckCircleIcon style={{ color: '#59B863' }} className="float-right" />
            ) : (
                <CircleIcon style={{ color: 'gray' }} />
            )}
        </QontoStepIconRoot>
    );
}

function UpgradeActions({ order }) {
    const { setUpgradeDialog } = useContext(Context);
    const { location_type } = genAttributes(order);

    return (
        <StepContent TransitionProps={{ in: true }}>
            <Grid
                container
                direction="row"
                css={css`
                    gap: 1rem;
                `}
            >
                <Grid item>
                    <PrimaryButton onClick={() => setUpgradeDialog({ type: 'haulaway_upgrade' })}>
                        Add Haulaway
                    </PrimaryButton>
                </Grid>
                {order?.[location_type] !== 'whiteGlove' && (
                    <Grid item>
                        <PrimaryButton onClick={() => setUpgradeDialog({ type: 'service_upgrade' })}>
                            Upgrade Service Level
                        </PrimaryButton>
                    </Grid>
                )}
            </Grid>
        </StepContent>
    );
}

function PendingPieceCount() {
    return (
        <StepContent>
            <Grid
                container
                direction="column"
                css={css`
                    font-weight: 500;
                `}
            >
                <Grid item>
                    <Typography
                        variant="body2"
                        css={css`
                            font-weight: 700;
                        `}
                    >
                        Awaiting Shipper Approval
                    </Typography>
                </Grid>

                <Grid>
                    <Typography
                        variant="body2"
                        css={css`
                            font-weight: 700;
                            color: ${colors.greys.subtitle};
                        `}
                    >
                        Reach out to shipper for updates.
                    </Typography>
                </Grid>
            </Grid>
        </StepContent>
    );
}

function ConfirmTimeframe({ confirmTimeframe }) {
    return (
        <StepContent>
            <Grid container direction="column">
                <Grid item lg={6}>
                    <Subtitle1>Please confirm the above timeframe.</Subtitle1>
                </Grid>
                <Grid item lg={6}>
                    <PrimaryButton onClick={confirmTimeframe}>Confirm Timeframe</PrimaryButton>
                </Grid>
            </Grid>
        </StepContent>
    );
}

export default function OrderStatus({ order, route, firstReview }) {
    const steps = useMemo(() => buildTrackerSteps({ order, route, firstReview }), [order, route, firstReview]);

    const [confirmTimeframe] = useMutation(UPDATE_ORDER_BY_ID, {
        variables: {
            order_id: order.order_id,
            update: {
                delivery_time_confirmed: 'confirmed',
                confirmation_source: CONFIRMATION_SOURCE.CUSTOMER,
            },
        },
        onError: (error) => {
            console.log(error);
            Sentry.captureException(error);
        },
    });

    const teamAssigned = steps.find((step) => step.key === STEP_KEYS.TEAM_ASSIGNED)?.completed;
    const hasBeenDelivered =
        ORDER_COMPLETE_STATUSES.includes(order.order_status) ||
        steps.find((step) => step.key === STEP_KEYS.DELIVERY_COMPLETE)?.completed;

    // Upgrades need the customer to have paid for the base order already, on their own order,
    // before it is delivered.
    const showUpgrades = !!order?.customer_rate && order?.source_form === 'CUSTOMER' && !hasBeenDelivered;
    const pendingPieceCount = !teamAssigned && order.source_form === 'CUSTOMER' && !order.piece_count_verified;
    const showConfirmTimeframe =
        !!order.del_window_start && // ftl check
        order.delivery_time_confirmed !== 'confirmed' &&
        !order?.order_shipper?.user?.circles['autoconfirm-all-stops'];

    return (
        <Stepper
            orientation="vertical"
            nonLinear
            css={css`
                padding: 12px;
            `}
        >
            {steps.map((step, index) => {
                // A step carrying `dateText` names a calendar date, not an instant — no time to show.
                const { date, time } = step.dateText ? { date: step.dateText, time: '' } : formatStamp(step.date);

                return (
                    <Step key={step.key} active={step.active} completed={step.completed}>
                        <StepLabel StepIconComponent={QontoStepIcon}>
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <Body2>
                                        <b>{step.label}</b>
                                    </Body2>
                                </Grid>
                                <Grid
                                    item
                                    css={css`
                                        text-align: right;
                                    `}
                                >
                                    <Body1>{date}</Body1>
                                    <br />
                                    <Body1>{time}</Body1>
                                </Grid>
                            </Grid>
                        </StepLabel>

                        {step.key === STEP_KEYS.OUT_FOR_DELIVERY && showUpgrades && <UpgradeActions order={order} />}

                        {pendingPieceCount && index === 0 && <PendingPieceCount />}

                        {step.key === STEP_KEYS.TIMEFRAME_CONFIRMED && showConfirmTimeframe && (
                            <ConfirmTimeframe confirmTimeframe={confirmTimeframe} />
                        )}
                    </Step>
                );
            })}
        </Stepper>
    );
}
